import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const { t } = useTranslation();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isHome = location.pathname === '/';

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  const navLinks = [
    { label: t('nav.home'), href: '/', internal: true, icon: 'ri-home-4-line' },
    { label: t('nav.categories'), href: '#categories', internal: false, icon: 'ri-grid-line' },
    { label: t('nav.shoppingGuide'), href: '/allchinabuy-spreadsheet', internal: true, icon: 'ri-book-open-line' },
    { label: t('nav.shipping'), href: '#info', internal: false, icon: 'ri-truck-line' },
    { label: t('nav.faq'), href: '#faq', internal: false, icon: 'ri-question-line' },
  ];

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 border-b border-background-200 bg-background-50/95 backdrop-blur">
        <div className="mx-auto flex h-16 items-center justify-between px-4 md:px-6 max-w-7xl">
          <Link to="/" className="flex items-center gap-2 whitespace-nowrap cursor-pointer">
            <span className="w-9 h-9 rounded-lg bg-primary-500 flex items-center justify-center">
              <i className="ri-table-line text-background-50 w-5 h-5 flex items-center justify-center" />
            </span>
            <span className="font-heading font-bold text-foreground-950 leading-tight">
              AllChinaBuy
              <span className="block text-xs font-medium text-foreground-600">Spreadsheet</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) =>
              link.internal ? (
                <Link
                  key={link.label}
                  to={link.href}
                  className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md transition-colors whitespace-nowrap cursor-pointer ${
                    isHome && link.href === '/'
                      ? 'text-primary-700 bg-primary-50'
                      : 'text-foreground-900 hover:text-primary-700 hover:bg-primary-50'
                  }`}
                >
                  <i className={`${link.icon} w-4 h-4 flex items-center justify-center`} />
                  {link.label}
                </Link>
              ) : (
                <a
                  key={link.label}
                  href={link.href}
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-foreground-900 hover:text-primary-700 hover:bg-primary-50 rounded-md transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className={`${link.icon} w-4 h-4 flex items-center justify-center`} />
                  {link.label}
                </a>
              )
            )}
          </nav>

          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="hidden sm:inline-flex items-center gap-2 rounded-full bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              {t('nav.cta')}
              <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center" />
            </a>
            <button
              type="button"
              onClick={() => setMobileOpen((v) => !v)}
              className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-md border border-background-200 bg-background-50 text-foreground-900 hover:bg-background-100 transition-colors cursor-pointer"
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            >
              <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} w-5 h-5 flex items-center justify-center`} />
            </button>
          </div>
        </div>
      </header>

      {mobileOpen && (
        <div className="lg:hidden fixed inset-x-0 top-16 bottom-0 z-40 overflow-y-auto bg-background-50 border-t border-background-200">
          <div className="px-4 py-5 pb-10">
            <nav className="space-y-1">
              {navLinks.map((link) =>
                link.internal ? (
                  <Link
                    key={link.label}
                    to={link.href}
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-3 rounded-lg px-4 py-3 text-base font-semibold text-foreground-900 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer"
                  >
                    <i className={`${link.icon} w-5 h-5 flex items-center justify-center text-primary-600`} />
                    {link.label}
                  </Link>
                ) : (
                  <a
                    key={link.label}
                    href={link.href}
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-3 rounded-lg px-4 py-3 text-base font-semibold text-foreground-900 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer"
                  >
                    <i className={`${link.icon} w-5 h-5 flex items-center justify-center text-primary-600`} />
                    {link.label}
                  </a>
                )
              )}
            </nav>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="mt-4 flex items-center justify-center gap-2 rounded-full bg-primary-500 px-4 py-3 text-sm font-semibold text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              {t('nav.cta')}
              <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center" />
            </a>

            <p className="mt-6 mb-3 text-xs font-semibold uppercase tracking-wide text-foreground-500">
              {t('nav.categories')}
            </p>
            <div className="grid grid-cols-2 gap-2">
              {categories.map((c) => (
                <a
                  key={c.key}
                  href={c.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-2 rounded-lg border border-background-200 bg-background-50 px-3 py-2.5 hover:border-primary-300 hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <img src={c.image} alt={t(`cat.${c.key}`)} className="h-9 w-9 shrink-0 rounded-md object-cover object-top" />
                  <span className="text-sm font-medium text-foreground-900 truncate">{t(`cat.${c.key}`)}</span>
                </a>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}